import { useEffect, useRef, type CSSProperties, type RefObject } from 'react';

import {
    AD_SIZES,
    adUtmCampaign,
    fillAdSlots,
    resolveAdCtaColor,
    resolveAdCtaTextColor,
    withAdUtm,
    type AdSizeKey,
    type AdSlotDef,
    type FilledAdSlot,
} from '@/lib/ads';
import { trackAdClick, trackAdImpression } from '@/lib/analytics';
import type { Ad } from '@/lib/content-types';
import { cn } from '@/lib/utils';

const HEBREW_CHAR_RE = /[\u0590-\u05FF]/;

/**
 * Pick flex/text direction from the creative copy (not the page).
 * Hebrew ads stay RTL; Latin-only ads flip to LTR so logo starts left and CTA ends right.
 * Ignores badge/sponsor - those are often Hebrew disclosure or Latin brand names on either side.
 */
function adCreativeDir(ad: Ad): 'ltr' | 'rtl' {
    const sample = [ad.title, ad.ctaLabel, ad.description, ad.secondaryCtaLabel]
        .filter(Boolean)
        .join(' ');
    if (!sample.trim()) return 'rtl';
    return HEBREW_CHAR_RE.test(sample) ? 'rtl' : 'ltr';
}

export function hasAdCreative(filled: FilledAdSlot<Ad>): boolean {
    const { ad } = filled;
    if (!ad) return false;
    if (ad.creativeType === 'text') return true;
    return Boolean(ad.imageUrl);
}

function adTrackMeta(ad: Ad, slot: AdSlotDef) {
    return {
        placement: ad.placement,
        slotId: slot.id,
        size: slot.size,
    };
}

/** Destination URL with default ad UTM params when none were set upfront. */
function adHref(url: string | undefined, ad: Ad): string | undefined {
    if (!url || url === '#') return undefined;
    return withAdUtm(url, {
        campaign: adUtmCampaign(ad),
        content: ad.placement,
    });
}

/** Record one viewable impression when ~50% of the unit is on screen. */
function useAdImpression(
    ad: Ad,
    slot: AdSlotDef,
    rootRef: RefObject<HTMLElement | null>,
) {
    const adId = ad.$id;
    const placement = ad.placement;
    const slotId = slot.id;
    const size = slot.size;

    useEffect(() => {
        const el = rootRef.current;
        if (!el || typeof IntersectionObserver === 'undefined') return;

        let sent = false;
        const observer = new IntersectionObserver(
            (entries) => {
                const entry = entries[0];
                if (!entry?.isIntersecting || sent) return;
                if (entry.intersectionRatio < 0.5) return;
                sent = true;
                trackAdImpression(adId, { placement, slotId, size });
                observer.disconnect();
            },
            { threshold: [0.5] },
        );
        observer.observe(el);
        return () => observer.disconnect();
    }, [adId, placement, slotId, size, rootRef]);
}

/** Native text / logo ad inside a fixed IAB size frame. */
function TextAdCreative({
    ad,
    size,
    slot,
}: {
    ad: Ad;
    size: AdSizeKey;
    slot: AdSlotDef;
}) {
    const tall = size === '300x600';
    const banner =
        size === '970x90' ||
        size === '728x90' ||
        size === '320x50' ||
        size === '320x100';
    const dir = adCreativeDir(ad);
    const badge = ad.badge ?? 'מודעה';
    const ctaLabel = ad.ctaLabel ?? 'לפרטים';
    const ctaColor = resolveAdCtaColor(ad.ctaColor);
    const ctaStyle = {
        '--ad-cta': ctaColor,
        backgroundColor: 'var(--ad-cta)',
        borderColor: 'var(--ad-cta)',
        color: resolveAdCtaTextColor(ad.ctaTextColor, ad.ctaColor),
    } as CSSProperties;
    const headline = ad.title ?? ad.name;
    const hasSecondary = Boolean(ad.secondaryCtaLabel && ad.secondaryCtaUrl);
    const primaryHref = adHref(ad.linkUrl, ad);
    const secondaryHref = adHref(ad.secondaryCtaUrl, ad);
    const track = adTrackMeta(ad, slot);

    const ctaClass = cn(
        'inline-flex items-center justify-center border font-medium transition-[filter] hover:brightness-110',
        tall ? 'px-3.5 py-2.5 text-base' : 'px-3 py-1.5 text-sm',
        banner && 'px-3 py-1.5 text-xs sm:text-sm',
    );
    const secondaryCtaClass = cn(
        'inline-flex items-center justify-center border border-white/15 text-white/55 transition-colors hover:border-white/30 hover:text-white',
        tall ? 'px-3.5 py-2.5 text-base' : 'px-3 py-1.5 text-sm',
        banner && 'px-3 py-1.5 text-xs sm:text-sm',
    );

    const primaryCta = primaryHref ? (
        <a
            href={primaryHref}
            className={ctaClass}
            style={ctaStyle}
            rel="sponsored nofollow noopener"
            target="_blank"
            onClick={() => trackAdClick(ad.$id, ad.placement, track)}
        >
            {ctaLabel}
        </a>
    ) : (
        <span className={ctaClass} style={ctaStyle}>
            {ctaLabel}
        </span>
    );

    const secondaryCta =
        hasSecondary && secondaryHref ? (
            <a
                href={secondaryHref}
                className={secondaryCtaClass}
                rel="sponsored nofollow noopener"
                target="_blank"
                onClick={() => trackAdClick(ad.$id, ad.placement, track)}
            >
                {ad.secondaryCtaLabel}
            </a>
        ) : null;

    if (banner) {
        return (
            <div
                dir={dir}
                className="flex size-full items-center gap-4 px-5 py-3 text-start sm:gap-5 sm:px-6"
            >
                {ad.logoUrl ? (
                    <img
                        src={ad.logoUrl}
                        alt=""
                        width={40}
                        height={40}
                        className="size-10 shrink-0 border border-white/10 bg-white/5 object-contain"
                        loading="lazy"
                    />
                ) : null}
                <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                        {ad.sponsorName && (
                            <p className="truncate text-xs text-white/55">{ad.sponsorName}</p>
                        )}
                        <span className="shrink-0 text-[10px] tracking-wide text-white/55">
                            {badge}
                        </span>
                    </div>
                    <p className="truncate text-sm font-normal text-white sm:text-[15px]">
                        {headline}
                    </p>
                </div>
                {hasSecondary ? (
                    <div className="flex shrink-0 gap-1.5">
                        {primaryCta}
                        {secondaryCta}
                    </div>
                ) : (
                    <span className={cn(ctaClass, 'shrink-0')} style={ctaStyle}>
                        {ctaLabel}
                    </span>
                )}
            </div>
        );
    }

    return (
        <div
            dir={dir}
            className={cn(
                'flex size-full flex-col px-4 text-start',
                tall ? 'gap-4 py-5' : 'justify-between gap-2 py-4',
            )}
        >
            <div className="flex shrink-0 items-start justify-between gap-3">
                <div className="flex min-w-0 items-center gap-3">
                    {ad.logoUrl ? (
                        <img
                            src={ad.logoUrl}
                            alt=""
                            width={tall ? 48 : 40}
                            height={tall ? 48 : 40}
                            className={cn(
                                'shrink-0 border border-white/10 bg-white/5 object-contain',
                                tall ? 'size-12' : 'size-10',
                            )}
                            loading="lazy"
                        />
                    ) : (
                        <div
                            className={cn(
                                'shrink-0 border border-white/10 bg-white/5',
                                tall ? 'size-12' : 'size-10',
                            )}
                            aria-hidden
                        />
                    )}
                    {ad.sponsorName && (
                        <p className="truncate text-sm text-white/55">{ad.sponsorName}</p>
                    )}
                </div>
                <span className="shrink-0 text-xs tracking-wide text-white/55">{badge}</span>
            </div>

            <div
                className={cn(
                    'min-w-0',
                    tall ? 'flex flex-1 flex-col' : undefined,
                )}
            >
                <h3
                    className={cn(
                        'title-single-line font-normal leading-snug tracking-tight text-white',
                        tall ? 'text-xl' : 'text-base',
                    )}
                >
                    {headline}
                </h3>
                {ad.description && (
                    <p
                        className={cn(
                            'leading-relaxed text-white/55',
                            tall
                                ? 'mt-3 flex-1 text-[15px] leading-7 line-clamp-12'
                                : 'mt-2 line-clamp-3 text-sm',
                        )}
                    >
                        {ad.description}
                    </p>
                )}
            </div>

            <div
                className={cn(
                    'flex shrink-0 flex-wrap gap-2',
                    tall && 'flex-col gap-2.5',
                )}
            >
                {hasSecondary ? (
                    <>
                        {primaryCta}
                        {secondaryCta}
                    </>
                ) : (
                    <span className={ctaClass} style={ctaStyle}>
                        {ctaLabel}
                    </span>
                )}
            </div>
        </div>
    );
}

export function AdSlot({
    filled,
    className,
}: {
    filled: FilledAdSlot<Ad>;
    className?: string;
}) {
    const { slot, ad } = filled;
    if (!hasAdCreative(filled) || !ad) return null;
    return <AdSlotTracked ad={ad} slot={slot} className={className} />;
}

function AdSlotTracked({
    ad,
    slot,
    className,
}: {
    ad: Ad;
    slot: AdSlotDef;
    className?: string;
}) {
    const rootRef = useRef<HTMLDivElement>(null);
    useAdImpression(ad, slot, rootRef);

    const dims = AD_SIZES[slot.size as AdSizeKey];
    const isText = ad.creativeType === 'text';
    const track = adTrackMeta(ad, slot);

    const frame = (
        <div
            ref={rootRef}
            className={cn(
                'mx-auto w-full max-w-full overflow-hidden border border-white/10 bg-black font-sans',
                className,
            )}
            style={{
                maxWidth: dims.width,
                aspectRatio: `${dims.width} / ${dims.height}`,
            }}
            aria-label={ad.name}
        >
            {isText ? (
                <TextAdCreative
                    ad={ad}
                    size={slot.size as AdSizeKey}
                    slot={slot}
                />
            ) : (
                <img
                    src={ad.imageUrl}
                    alt={ad.name}
                    width={dims.width}
                    height={dims.height}
                    className="size-full object-cover"
                    loading="lazy"
                />
            )}
        </div>
    );

    if (isText && ad.secondaryCtaLabel && ad.secondaryCtaUrl) {
        return frame;
    }

    const wrapHref = adHref(ad.linkUrl, ad);
    if (wrapHref) {
        return (
            <a
                href={wrapHref}
                className="block"
                rel="sponsored nofollow noopener"
                target="_blank"
                onClick={() => trackAdClick(ad.$id, ad.placement, track)}
            >
                {frame}
            </a>
        );
    }

    return frame;
}

export function AdSlotBlock({
    filled,
    className,
}: {
    filled: FilledAdSlot<Ad>;
    className?: string;
}) {
    if (!hasAdCreative(filled)) return null;

    return (
        <div className={cn('border-b border-white/10 px-6 py-6', className)}>
            <AdSlot filled={filled} />
        </div>
    );
}

/** Fill slots and render only creatives that have content. */
export function AdPlacementRail({
    slots,
    ads,
    className,
    itemClassName,
}: {
    slots: readonly AdSlotDef[];
    ads: readonly Ad[];
    className?: string;
    itemClassName?: string;
}) {
    const filled = fillAdSlots(slots, ads).filter(hasAdCreative);
    if (filled.length === 0) return null;

    return (
        <div className={className} aria-label="מודעה">
            {filled.map((item) => (
                <div key={item.slot.id} className={itemClassName}>
                    <AdSlot filled={item} />
                </div>
            ))}
        </div>
    );
}
